import { useMemo } from 'react'
import { TradingBalance, useRateConvert, useTickerBatch, useWallet } from '@web/walter-lib/stream'
import { Dictionary, keyBy } from 'lodash'
import { Balance, GetRatesBatchReturn } from '@web/walter-lib/api'
import { usePrevCurrentRatesBatch } from './usePrevCurrentRatesBatch'
import { estimateBalances, PriceType } from '~utils/data/processBalance'
import {
    useCommonCMSConfig,
    useCurrencies,
    useFuturesTradingBalances,
    useGetWalletBalance,
    useSpotTradingBalances,
    useVcardsBalances,
} from '~api'

export type NewRatesByCurrency = Record<string, { current?: string | null; yesterday?: string | null }>

export interface BalanceMap {
    wallet: ReturnType<typeof useGetWalletBalance>['data'] | ReturnType<typeof useWallet>['data']
    vcards: Dictionary<Balance>
    spot: Dictionary<TradingBalance>
    futures: Dictionary<TradingBalance>
}

// eslint-disable-next-line complexity
export function useEstimatedBalances(toCurrency: string, priceType: PriceType = 'close') {
    const { data: currencies } = useCurrencies()
    const { data, isValidating: isConfigLoading } = useCommonCMSConfig()
    const { isFuturesAvailable, showVirtualCard, isNewRateService, blockTradingSocket, showLockedBalance } =
        data?.features || {}

    //TODO: if wallet empty => sub message will not be sent by server => loading forever
    const { data: wallet } = useWallet()
    const { data: walletData, isLoading: isWalletDataLoading } = useGetWalletBalance()

    const { data: vcards, error: vcardsError } = useVcardsBalances({
        shouldRequest: showVirtualCard && !isConfigLoading,
    })
    const { data: spot, error: spotError } = useSpotTradingBalances()
    const { data: futures, error: futuresError } = useFuturesTradingBalances({
        shouldRequest: isFuturesAvailable && !isConfigLoading,
    })

    const isVcards = !!vcardsError
    const isSpot = !!spotError || !!spot || blockTradingSocket
    const isFutures = !!futuresError || !!futures || (!isFuturesAvailable && !isConfigLoading) || blockTradingSocket

    const vcardsBalances = useMemo(() => keyBy(vcards, 'currency'), [vcards])
    const futuresBalances = useMemo(() => keyBy(futures, 'currency'), [futures])
    const spotBalances = useMemo(() => keyBy(spot, 'currency'), [spot])

    const balancesMap: BalanceMap = {
        wallet: wallet || walletData || {},
        vcards: vcardsBalances,
        spot: spotBalances,
        futures: futuresBalances,
    }

    const { data: tickers } = useTickerBatch({
        symbols: ['*'],
        speed: '3s',
    })
    const rateConvert = useRateConvert()

    const {
        ratesData,
        prevRatesData,
        isLoading: isRatesLoading,
    } = usePrevCurrentRatesBatch({
        isNewRateService,
        currencies: Object.keys(currencies || {}),
        quote: toCurrency,
    })

    const rates = getLastTwoPrices({ ratesData, prevRatesData })

    const {
        wallet: walletEstimate,
        vcards: vcardsEstimate,
        spot: spotEstimate,
        futures: futuresEstimate,
        estimated,
    } = estimateBalances(
        balancesMap,
        toCurrency,
        rateConvert,
        priceType,
        Boolean(isNewRateService),
        Boolean(showLockedBalance),
        rates
    )

    return {
        wallet: {
            total: walletEstimate,
            loaded: (isNewRateService && !isRatesLoading) || !!tickers,
        },
        vcards: {
            total: vcardsEstimate,
            loaded: isVcards,
        },
        spot: {
            total: spotEstimate,
            loaded: !!(isSpot && (tickers || (isNewRateService && !isRatesLoading))),
        },
        futures: {
            total: futuresEstimate,
            loaded: !!(isFutures && (tickers || (isNewRateService && !isRatesLoading))),
        },
        balance: {
            total: estimated,
            loaded:
                !isWalletDataLoading &&
                !!(isSpot && isFutures && (tickers || (isNewRateService && !isRatesLoading && ratesData))),
        },
        balancesMap,
    }
}

interface Props {
    ratesData: GetRatesBatchReturn | undefined
    prevRatesData?: GetRatesBatchReturn | undefined
}

export function getLastTwoPrices({ ratesData, prevRatesData }: Props) {
    const result: NewRatesByCurrency = {}

    ratesData?.[0]?.prices?.forEach(({ price, base }) => {
        if (base) {
            result[base] = { current: price ?? null }
        }
    })

    prevRatesData?.[0]?.prices?.forEach(({ price, base }) => {
        if (base) {
            result[base] = { ...(result[base] ?? {}), yesterday: price ?? null }
        }
    })

    return result
}
